import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCustomerAuth } from "@/lib/customerAuth";
import SEO from "@/components/SEO";
import RateLockBadge from "@/components/RateLockBadge";
import {
  Package,
  Clock,
  CheckCircle2,
  DollarSign,
  LogOut,
  Plus,
  ChevronRight,
  XCircle,
  Mail,
} from "lucide-react";

const STATUS_LABEL = {
  submitted: "Pending review",
  under_review: "Under review",
  approved: "Approved",
  rejected: "Rejected",
  paid: "Paid",
};

const STATUS_TONE = {
  submitted: "amber",
  under_review: "amber",
  approved: "emerald",
  rejected: "red",
  paid: "emerald",
};

const TONE_CHIP = {
  emerald: "bg-[#346af8]/10 text-[#346af8] border-[#346af8]/20",
  amber: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  red: "bg-red-500/10 text-red-700 border-red-500/30",
  ink: "bg-[#0b0d17]/8 text-[#0b0d17] border-[#0b0d17]/15",
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function CustomerDashboardPage() {
  const { customer, loading: authLoading, signOut } = useCustomerAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!customer) {
      navigate("/signin?next=/dashboard");
      return;
    }
    api
      .get("/customer/submissions")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [authLoading, customer, navigate]);

  if (authLoading || loading || !customer) {
    return (
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14 text-sm text-[#9a93a6]">
        Loading your dashboard…
      </section>
    );
  }

  const stats = data?.stats || {};
  const items = data?.items || [];

  return (
    <>
      <SEO title="My dashboard" />
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8 md:mb-10 flex-wrap">
          <div>
            <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#346af8]">
              Dashboard
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0b0d17] mt-2 tracking-tight">
              Hi {customer.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="mt-2 text-sm text-[#6c6376] inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {customer.email}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/submit"
              data-testid="dashboard-new-submission"
              className="h-10 px-4 rounded-full bg-[#346af8] text-white text-sm font-semibold hover:bg-[#2554d6] inline-flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> New submission
            </Link>
            <button
              onClick={() => { signOut(); navigate("/"); }}
              data-testid="dashboard-signout"
              className="h-10 px-4 rounded-full border border-[#e8e8ef] text-[#6c6376] text-sm font-semibold hover:bg-white inline-flex items-center gap-2 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat
            label="Total submissions"
            value={stats.total ?? 0}
            Icon={Package}
            tone="ink"
            testid="stat-total"
          />
          <Stat
            label="Pending"
            value={stats.pending ?? 0}
            Icon={Clock}
            tone="amber"
            testid="stat-pending"
          />
          <Stat
            label="Approved"
            value={stats.approved ?? 0}
            Icon={CheckCircle2}
            tone="emerald"
            testid="stat-approved"
          />
          <Stat
            label="Total value"
            value={`$${(stats.total_value || 0).toFixed(2)}`}
            Icon={DollarSign}
            tone="emerald"
            testid="stat-value"
          />
        </div>

        {/* Submissions */}
        <div className="bg-white border border-[#e8e8ef] rounded-2xl soft-shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e8e8ef] flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-[#0b0d17]">
              My submissions
            </h2>
            <span className="text-xs text-[#9a93a6]">
              {items.length} record{items.length === 1 ? "" : "s"}
            </span>
          </div>

          {items.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="divide-y divide-[#e8e8ef]">
              {items.map((s) => (
                <li key={s.id} data-testid={`sub-row-${s.reference}`}>
                  <Link
                    to={`/track?ref=${encodeURIComponent(s.reference)}&email=${encodeURIComponent(customer.email)}`}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-[#f4f4f8] transition-colors group"
                  >
                    <div className="shrink-0 w-10 h-10 rounded-full bg-[#f4f4f8] border border-[#e8e8ef] flex items-center justify-center">
                      <Package className="w-4 h-4 text-[#6c6376]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-display font-semibold text-[#0b0d17] text-sm">
                        {s.reference}
                      </div>
                      <div className="text-xs text-[#9a93a6] mt-0.5 truncate">
                        {formatDate(s.created_at)} · {s.cards?.length || 0} card{s.cards?.length === 1 ? "" : "s"}
                        {s.cards?.[0]?.brand_name ? ` · ${s.cards[0].brand_name}` : ""}
                        {(s.cards?.length || 0) > 1 ? ` +${s.cards.length - 1}` : ""}
                      </div>
                      {s.rate_locked_until && (s.status === "submitted" || s.status === "under_review") && (
                        <div className="mt-1.5 hidden sm:block">
                          <RateLockBadge until={s.rate_locked_until} compact />
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0 hidden sm:block">
                      <div className="text-xs text-[#9a93a6]">Est. payout</div>
                      <div className="font-display font-semibold text-[#0b0d17]">
                        ${(s.estimated_payout || 0).toFixed(2)}
                      </div>
                    </div>
                    <StatusChip status={s.status} />
                    <ChevronRight className="w-4 h-4 text-[#9a93a6] group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}

function Stat({ label, value, Icon, tone, testid }) {
  const toneRing = {
    emerald: "bg-[#346af8]/10 text-[#346af8]",
    amber: "bg-amber-500/10 text-amber-600",
    ink: "bg-[#0b0d17]/8 text-[#0b0d17]",
  }[tone];
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#e8e8ef] bg-white p-4 md:p-5 soft-shadow"
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${toneRing} mb-3`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="font-display text-2xl md:text-3xl font-bold text-[#0b0d17] leading-none">
        {value}
      </div>
      <div className="text-xs text-[#9a93a6] mt-1.5 uppercase tracking-wider font-semibold">
        {label}
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const tone = STATUS_TONE[status] || "ink";
  const Icon = status === "rejected" ? XCircle : status === "paid" || status === "approved" ? CheckCircle2 : Clock;
  return (
    <span
      className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-semibold border ${TONE_CHIP[tone]}`}
    >
      <Icon className="w-3 h-3" />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-12 text-center" data-testid="dashboard-empty">
      <div className="inline-flex w-12 h-12 rounded-full bg-[#f4f4f8] border border-[#e8e8ef] items-center justify-center mb-3">
        <Package className="w-5 h-5 text-[#9a93a6]" />
      </div>
      <p className="text-sm text-[#6c6376] font-medium">No submissions yet.</p>
      <p className="text-xs text-[#9a93a6] mt-1 mb-4">
        Submit your first card and it'll show up here instantly.
      </p>
      <Link
        to="/submit"
        className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#346af8] text-white text-sm font-semibold hover:bg-[#2554d6] transition-colors"
      >
        <Plus className="w-4 h-4" /> Start a submission
      </Link>
    </div>
  );
}
